import React, { useEffect, useState } from "react";
import { View, TouchableOpacity } from "react-native";

import Text from "components/Text";
import Icon from "components/Icon";
import Input from "components/Input";
import Empty from "components/Empty";
import Error from "components/Error";
import Loading from "components/Loading";
import ModalInfinityList from "components/ModalInfinityList";
import AnimatedInfinityList from "components/AnimatedInfinityList";

import { styles } from "./styles";
import { sizes, colors, strings } from "assets/constants";

import { useFlag } from "hooks";
import { liveSearchInput, mq } from "utils";

import { useProducts } from "clients/apis";

let timer: ReturnType<typeof setTimeout>;

export default function ProductPicker(props: PickerT) {
  const { id, value, onSelect, error, hideError, onPress, disabled } = props;
  const val = value as ProductT;

  const [title, setTitle] = useState("");
  const {
    isError,
    error: productsError,
    isRefetching,
    isLoading,
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProducts({ title, categoryId: id });

  const { flg: modal, on: show, off: hide } = useFlag();
  const { flg: typing, on: startTyping, off: stopTyping } = useFlag();

  const _onHide = () => {
    setTitle("");
    hide();
  };

  const [valueState, setValueState] = useState<ProductT | undefined>();

  useEffect(() => {
    setValueState(val);
  }, [val]);

  useEffect(() => {
    id && refetch();
  }, [id, refetch]);

  const _renderItem = (items: []) => {
    return items.map((product: ProductT, key: number) => (
      <React.Fragment key={String(key)}>
        <TouchableOpacity
          style={styles.item}
          onPress={() => {
            onSelect && onSelect(product);
            setTitle("");
            setValueState(product);
            hide();
          }}
        >
          <Text>{product.title}</Text>
        </TouchableOpacity>
        <View style={styles.itemSeparator} />
      </React.Fragment>
    ));
  };

  const _renderList = () => {
    if (isLoading || typing) {
      return <Loading style={{ marginTop: mq(20) }} />;
    }

    if (isError) {
      return (
        <Error error={productsError} reset={refetch} loading={isRefetching} />
      );
    }

    if (data?.pages?.[0]?.data?.data?.length === 0) {
      return <Empty message={strings.no_product} />;
    }

    return (
      <AnimatedInfinityList
        onRefresh={refetch}
        responseData={data}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        renderItemList={_renderItem}
      />
    );
  };

  return (
    <>
      <>
        <TouchableOpacity
          disabled={disabled}
          onPress={onPress ?? show}
          activeOpacity={sizes.tapOpacity}
          style={{ ...styles.container }}
        >
          <View style={styles.iconContainer}>
            <Icon name="chevron-down" />
          </View>
          <View style={styles.textContainer}>
            <Text
              color={
                valueState?.title ? colors.textColorDefault : colors.placeholder
              }
            >
              {valueState?.title || strings.pick_product}
            </Text>
          </View>

          <View style={styles.iconContainer}>
            <Icon name="box" />
          </View>
        </TouchableOpacity>

        {!hideError && (
          <View style={styles.errorContainer}>
            <Text size={"xs"} color={colors.danger}>
              {error}
            </Text>
          </View>
        )}
      </>

      <ModalInfinityList visible={modal} close={_onHide}>
        <View style={styles.listSearchContainer}>
          <Input
            hideError
            icon={{ name: "search" }}
            placeholder={strings.search}
            onChangeText={(inputName) => {
              liveSearchInput(
                inputName,
                title,
                setTitle,
                timer,
                startTyping,
                stopTyping
              );
              refetch();
            }}
          />
        </View>
        {_renderList()}
      </ModalInfinityList>
    </>
  );
}
